import { createContext, forwardRef, useContext, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'
import { contentClasses, type ContentProps } from '@/lib/content-props'

/**
 * A card's states are props (30.10.2026). Pages used to paint them by hand: a green edge for the
 * running event in two places with two values, a red danger zone in two places with two values,
 * a selected drop target, a bare embedded card. Each is named once here.
 */
const CARD_TONE = {
  default: '',
  /** Happening now: a green edge, no fill. */
  live: 'border-[var(--color-success-500)]/40',
  /** Done and good (an accepted offer): green edge and a faint green fill. */
  success: 'border-[var(--color-success-200)] bg-[var(--color-success-50)]/40',
  /** A danger zone (delete event, delete account). */
  danger: 'border-[var(--status-error-text)]/30',
  /** The selected or targeted one (allocation drop zones). */
  selected: 'border-[var(--border-selected)] bg-[var(--surface-muted)]',
  /** Embedded in another surface: no edge, fill or shadow of its own. */
  bare: 'rounded-none border-0 bg-transparent shadow-none',
} as const
const CARD_PADDING = { none: '', sm: 'p-3', md: 'p-4' } as const

export type CardTone = keyof typeof CARD_TONE

const Card = forwardRef<HTMLDivElement, (HTMLAttributes<HTMLDivElement> & {
  interactive?: boolean
  tone?: CardTone
  /** For a card whose children are not CardHeader/CardContent (drag targets). */
  padding?: keyof typeof CARD_PADDING
  /** A drop zone or placeholder edge. */
  dashed?: boolean
  /** Ring the card while a control inside it has keyboard focus (a card that is one link). */
  focusWithin?: boolean
}) & ContentProps>(
  ({ className, align, clamp, wrap, numeric, focus, interactive, tone = 'default', padding = 'none', dashed = false, focusWithin = false, ...props }, ref) => (
    <div
      ref={ref}
      /* Shared Parley panel treatment. The earlier hairline-only contract was
         superseded on 24.10.2026: Card and BentoCard now share the same token
         edge and restrained shadow. RowGroup uses that edge without elevation. */
      className={cn(
        // The raised edge `BentoCard` has (sweep 24.10.2026): two card primitives looked like two
        // products, flat on settings and the editor, raised on the dashboard. One card, one edge.
        'rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--card-shadow)] overflow-hidden',
        interactive && [
          'transition-all duration-[var(--duration-base)] ease-[var(--ease-out)]',
          'hover:border-[var(--card-hover-border)]',
          'hover:-translate-y-0.5 cursor-pointer',
        ],
        CARD_TONE[tone],
        CARD_PADDING[padding],
        dashed && 'border-dashed',
        focusWithin && 'focus-within:ring-2 focus-within:ring-[var(--focus-ring-color)]',
        contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'Card', props)
      )}
      {...props}
    />
  )
)
Card.displayName = 'Card'

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { inset?: 'default' | 'flush' }>(
  ({ className, inset = 'default', ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col gap-1.5', inset === 'flush' ? 'p-0' : 'p-[var(--card-padding)]', placed(className, 'CardHeader', props))}
      {...props}
    />
  )
)
CardHeader.displayName = 'CardHeader'

/**
 * The level a card title takes, decided by where the card sits rather than by
 * each page. A card is a top-level section under the page's `<h1>` by default,
 * so it is an `<h2>`. A container that already owns the `<h2>` (the settings
 * panel, whose name is its heading) provides 3 here, and every card inside it
 * nests under that name without a single page choosing a level by hand.
 */
const CardTitleLevelContext = createContext<2 | 3>(2)

/** Sets the level of every `CardTitle` below it. */
function CardTitleLevel({ level, children }: { level: 2 | 3; children: ReactNode }) {
  return <CardTitleLevelContext.Provider value={level}>{children}</CardTitleLevelContext.Provider>
}

/** A card title's size and ink are props, never page classes (30.10.2026: 35 pages restyled it). */
const CARD_TITLE_SIZE = { default: 'text-h3', sm: 'text-label', lg: 'text-h2' } as const
/** `inherit`: the colour of the surface it sits on (a bento hero or ink card). */
const CARD_TITLE_TONE = { default: 'text-[var(--text-primary)]', danger: 'text-[var(--status-error-text)]', inherit: 'text-inherit' } as const

const CardTitle = forwardRef<HTMLHeadingElement, (HTMLAttributes<HTMLHeadingElement> & {
  size?: keyof typeof CARD_TITLE_SIZE
  tone?: keyof typeof CARD_TITLE_TONE
  /** The display face (bento cards). */
  display?: boolean
  /** Bold and tight (status cards). */
  strong?: boolean
}) & ContentProps>(
  ({ className, align, clamp, wrap, numeric, focus, size = 'default', tone = 'default', display = false, strong = false, ...props }, ref) => {
    // <h2>, not <h3>, by default: a card title is a top-level section under the
    // page's <h1>, so h3 skipped a level (axe `heading-order` on 13 routes). The
    // `text-h3` class keeps the visual size identical at either level.
    const Heading = useContext(CardTitleLevelContext) === 3 ? 'h3' : 'h2'
    return <Heading ref={ref} className={cn(CARD_TITLE_SIZE[size], CARD_TITLE_TONE[tone], display && 'font-[family-name:var(--font-display)] font-semibold', strong && 'font-bold leading-tight', contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'CardTitle', props))} {...props} />
  }
)
CardTitle.displayName = 'CardTitle'

const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-small text-[var(--text-secondary)]', placed(className, 'CardDescription', props))} {...props} />
  )
)
CardDescription.displayName = 'CardDescription'

/**
 * The card's body. Its spacing is decided HERE, never by the page (30.10.2026).
 *
 * `default` pads the sides and bottom, and pads the top only when it is the card's first part:
 * under a `CardHeader` the header supplies the top edge, without one the content does. Until
 * 30.10.2026 the top was the page's job, and 161 pages wrote `pt-6`, `pt-5`, `pt-4` or `pt-0`
 * (`check-card-padding` existed only to catch the ones that forgot). That per-page spacing is
 * the class of drift behind David's two-tone card.
 *
 * `compact` is a dense row-card (a running event, a list summary), `roomy` a centred outcome
 * or empty block, `flush` a table or list that runs to the card's edges and owns its spacing.
 */
const CARD_CONTENT_INSET = {
  default: 'px-[var(--card-padding)] pb-[var(--card-padding)] first:pt-[var(--card-padding)]',
  compact: 'px-[var(--card-padding)] py-4',
  roomy: 'px-[var(--card-padding)] py-8',
  flush: 'p-0',
} as const

export type CardContentInset = keyof typeof CARD_CONTENT_INSET

const CardContent = forwardRef<HTMLDivElement, (HTMLAttributes<HTMLDivElement> & {
  inset?: CardContentInset
  /** A rule above: the second part of a card that has no header between its parts. */
  divided?: boolean
  /** Rows separated by rules (a flush list of items). */
  rows?: boolean
}) & ContentProps>(
  ({ className, align, clamp, wrap, numeric, focus, inset = 'default', divided = false, rows = false, ...props }, ref) => (
    <div ref={ref} className={cn(CARD_CONTENT_INSET[inset], divided && 'border-t border-[var(--border-default)]', rows && 'divide-y divide-[var(--border-default)]', contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'CardContent', props))} {...props} />
  )
)
CardContent.displayName = 'CardContent'

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { sticky?: boolean }>(
  ({ className, sticky = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center px-[var(--card-padding)]',
        /* `sticky`: a save bar that stays at the bottom of a long editor card. */
        sticky
          ? 'sticky bottom-0 z-20 rounded-b-[var(--card-radius)] border-t border-[var(--border-default)] bg-[var(--card-bg)] py-3'
          : 'pb-[var(--card-padding)]',
        placed(className, 'CardFooter', props)
      )}
      {...props}
    />
  )
)
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardTitle, CardTitleLevel, CardDescription, CardContent, CardFooter }
